import {Grid, Stack, Typography} from '@mui/material';
import {AppButton} from 'components/buttons';
import {AppDialog, AppDialogProps} from 'components/dialogs';
import {AppControlledDateTimePicker, AppControlledInput, AppItemDataSelect} from 'components/form';
import {AppControlledEditor} from 'components/form/AppTextEditor';
import React, {useEffect, useMemo} from 'react';

import {useCreateTimeTrackerEntity, useGetOneTimeTrackerEntry, useUpdateTimeTracker} from 'hooks';

import {TimeTrackerCreateUpdateRequest, timeTrackerDefaultValues, TimeTrackerModel, TimeTrackerStatus} from 'models';
import {useForm} from 'react-hook-form';
import {BoardDefaultStatusType} from 'models/board';
import AppTimerCounterTypography from 'components/typographies/AppTimerCounterTypography';
interface AddUpdateReminderDialogProps extends Omit<AppDialogProps, 'children'> {
    timeTrackerId?: string;
    afterUpdateAndCreateHandler: (item: TimeTrackerModel) => void;
    itemDataId?: string;
}

export const TimeTrackerAddUpdateManulEntryDialog: React.FC<AddUpdateReminderDialogProps> = ({
    timeTrackerId,
    afterUpdateAndCreateHandler,
    onCloseDialog,
    itemDataId,
    ...rest
}) => {
    const {
        setValue,
        control,
        handleSubmit,
        formState: {isValid},
        watch,
        reset,
    } = useForm<TimeTrackerCreateUpdateRequest>({
        defaultValues: {...timeTrackerDefaultValues, itemDataId: itemDataId},
    });
    const watchStartTime = watch('startTime');
    const watchEndTime = watch('endTime');
    const watchStatus = watch('status');

    const isEdit = useMemo(() => {
        return !!timeTrackerId;
    }, [timeTrackerId]);

    const {createTimeTrackerEntity, createTimeTrackerEntityLoading, createTimeTrackerEntityResponse} =
        useCreateTimeTrackerEntity();
    const {updateTimeTrackerEntity, updateTimeTrackerEntityLoading, updateTimeTrackerEntityResonse} =
        useUpdateTimeTracker();
    const {getOneTimeTrackerEntry, getOneTimeTrackerEntryLoading, getOneTimeTrackerEntryResponse} =
        useGetOneTimeTrackerEntry();

    const handleDialogClose = () => {
        reset(timeTrackerDefaultValues);
        onCloseDialog();
    };

    useEffect(() => {
        if (timeTrackerId) {
            getOneTimeTrackerEntry({
                variables: {
                    where: {
                        id: {eq: timeTrackerId},
                    },
                },
            });
        }
    }, [timeTrackerId]);

    const subHandler = (value: TimeTrackerCreateUpdateRequest) => {
        if (timeTrackerId && getOneTimeTrackerEntryResponse) {
            updateTimeTrackerEntity({
                variables: {
                    id: timeTrackerId,
                    input: {
                        ...value,
                        title: value.title,
                        status: getOneTimeTrackerEntryResponse.status,
                        itemDataId: value.itemDataId,
                        sprintId: getOneTimeTrackerEntryResponse.sprintId,
                        startTime: value.startTime instanceof Date ? value.startTime.toISOString() : value.startTime,
                        endTime: value.endTime instanceof Date ? value.endTime.toISOString() : value.endTime,
                    },
                },
            });
        } else {
            createTimeTrackerEntity({
                variables: {
                    input: {
                        ...value,
                        startTime: value.startTime instanceof Date ? value.startTime.toISOString() : value.startTime,
                        endTime: value.endTime instanceof Date ? value.endTime.toISOString() : value.endTime,
                    },
                },
            });
        }
    };

    useEffect(() => {
        if (getOneTimeTrackerEntryResponse && !getOneTimeTrackerEntryLoading) {
            setValue('description', getOneTimeTrackerEntryResponse.description);
            setValue('title', getOneTimeTrackerEntryResponse.title);
            setValue('startTime', new Date(getOneTimeTrackerEntryResponse.startTime));
            setValue(
                'endTime',
                getOneTimeTrackerEntryResponse.endTime ? new Date(getOneTimeTrackerEntryResponse.endTime) : undefined,
            );
            setValue('status', getOneTimeTrackerEntryResponse.status);
            setValue('itemDataId', getOneTimeTrackerEntryResponse.itemDataId);
            setValue('sprintId', getOneTimeTrackerEntryResponse.sprintId);
        }
    }, [getOneTimeTrackerEntryLoading, getOneTimeTrackerEntryResponse]);

    useEffect(() => {
        if (createTimeTrackerEntityResponse && !createTimeTrackerEntityLoading) {
            afterUpdateAndCreateHandler(createTimeTrackerEntityResponse);
            handleDialogClose();
            // onCloseDialog();
        }
    }, [createTimeTrackerEntityResponse, createTimeTrackerEntityLoading]);
    useEffect(() => {
        if (!updateTimeTrackerEntityLoading && updateTimeTrackerEntityResonse) {
            afterUpdateAndCreateHandler(updateTimeTrackerEntityResonse);
            handleDialogClose();

            // onCloseDialog();
        }
    }, [updateTimeTrackerEntityLoading, updateTimeTrackerEntityResonse]);

    return (
        <AppDialog
            title={`${timeTrackerId ? 'Update' : 'Add'} Manual Entry`}
            fullWidth
            {...rest}
            descriptionText={
                isEdit
                    ? 'Only manually added entries can be edited. Tracked hours and the title cannot be changed.'
                    : "Use this option to log time manually when you weren't able to use the timer. You can specify the start and end time, total hours worked, and add a brief description of the task completed."
            }
            onCloseDialog={handleDialogClose}
        >
            <form>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledInput
                                label={'Title'}
                                disabled={watchStatus !== TimeTrackerStatus.Manual}
                                placeholder="Enter Reminder Title"
                                name="title"
                                control={control}
                                required={'Title is required'}
                            />
                        </Stack>
                    </Grid>
                    {!itemDataId && (
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                                <AppItemDataSelect
                                    filters={{
                                        itemStatusTypes: [BoardDefaultStatusType.Open],
                                    }}
                                    label="Tickets"
                                    control={control}
                                    disabled={watchStatus !== TimeTrackerStatus.Manual}
                                    shouldReturnValueOnly={true}
                                    name="itemDataId"
                                />
                            </Stack>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledDateTimePicker
                                control={control}
                                disabled={watchStatus !== TimeTrackerStatus.Manual}
                                maxDate={
                                    watchEndTime
                                        ? watchEndTime instanceof Date
                                            ? watchEndTime
                                            : new Date(watchEndTime)
                                        : undefined
                                }
                                required={'Starting is required'}
                                label={'Starting time'}
                                name="startTime"
                            />
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledDateTimePicker
                                control={control}
                                disabled={watchStatus !== TimeTrackerStatus.Manual}
                                minDate={
                                    watchStartTime
                                        ? watchStartTime instanceof Date
                                            ? watchStartTime
                                            : new Date(watchStartTime)
                                        : undefined
                                }
                                required={'Ending  time is required'}
                                label={'Ending time'}
                                name="endTime"
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <Typography variant="caption">Spent Time</Typography>
                            {watchStartTime && watchEndTime && (
                                <AppTimerCounterTypography
                                    variant="h3"
                                    color={'primary.main'}
                                    counterValueInSeconds={Math.max(
                                        0,
                                        Math.floor(
                                            (new Date(watchEndTime).getTime() - new Date(watchStartTime).getTime()) /
                                                1000,
                                        ),
                                    )}
                                />
                            )}
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledEditor
                                control={control}
                                name="description"
                                label={'Description'}
                                key={'text-quill-editor'}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <AppButton
                                color="error"
                                variant="outlined"
                                onClick={() => {
                                    handleDialogClose();
                                }}
                            >
                                Cancel
                            </AppButton>
                            <AppButton
                                isLoading={createTimeTrackerEntityLoading || updateTimeTrackerEntityLoading}
                                isAnimated={isValid}
                                onClick={handleSubmit(subHandler)}
                                variant="contained"
                            >
                                {timeTrackerId ? 'Update' : 'Add'}
                            </AppButton>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </AppDialog>
    );
};

export default TimeTrackerAddUpdateManulEntryDialog;
